import { formatSingleQuarterLabel, getUniqueXQuarters } from "./utils";

export function buildYearQuarterAxisConfig(
  series,
  isYearQuarter: boolean,
  xDataType,
  lockedXMin,
  lockedXMax
) {
  const axisLabelSpacing = 3600 * 1000 * 24 * 365 * 0.25; // 3 months between axis
  const quarterToMonth: Record<string, string> = {
    Q1: "01-01",
    Q2: "04-01",
    Q3: "07-01",
    Q4: "10-01"
  };
  let lastShownQuarter = null;

  const uniqueXQuarters = getUniqueXQuarters(series, isYearQuarter, xDataType);
  const isYearQuarterSinglePoint = uniqueXQuarters.size === 1;

  const quarterTimestamps = [...uniqueXQuarters]
    .map((v) => {
      const [year, quarter] = v.split("-");
      const monthDay = quarterToMonth[quarter];
      return monthDay ? new Date(`${year}-${monthDay}`).getTime() : null;
    })
    .filter((ts): ts is number => Number.isFinite(ts))
    .sort((a, b) => a - b);

  const singleQuarterTimestamp: number | null = isYearQuarterSinglePoint
    ? quarterTimestamps[0] ?? null
    : null;

  // Pad by half a quarter on each side. A full quarter of padding lands only 18 hours
  // after the preceding calendar quarter boundary, causing ECharts to snap back and
  // emit a spurious extra tick. Half a quarter (~45 days) lands safely mid-quarter,
  // so ECharts never generates ticks outside the actual data range.
  const quarterPadding = axisLabelSpacing / 2;
  const yearQuarterAxisMin =
    quarterTimestamps.length > 0 && lockedXMin == null
      ? quarterTimestamps[0] - quarterPadding
      : lockedXMin;
  const yearQuarterAxisMax =
    quarterTimestamps.length > 0 && lockedXMax == null
      ? quarterTimestamps[quarterTimestamps.length - 1] + quarterPadding
      : lockedXMax;

  const monthToQuarter: Record<string, string> = {
    "01": "Q1",
    "04": "Q2",
    "07": "Q3",
    "10": "Q4"
  };

  const axisLabelFormatter = (value: number) => {
    const dateStr = new Date(value).toISOString().substring(0, 10);
    const [year, month] = dateStr.split("-");

    if (month !== "01" && month !== "04" && month !== "07" && month !== "10") {
      return "";
    }

    if (isYearQuarterSinglePoint && singleQuarterTimestamp != null) {
      return formatSingleQuarterLabel(
        year,
        month,
        singleQuarterTimestamp,
        monthToQuarter
      );
    }

    const currentQuarter = `${year}-${month}`;
    if (lastShownQuarter === currentQuarter) {
      return ""; // Skip duplicate
    }

    lastShownQuarter = currentQuarter;
    return ` ${year}-${monthToQuarter[month]} `; // one space at the front and back keeps labels consistent
  };

  return {
    axisLabelSpacing,
    quarterToMonth,
    yearQuarterAxisMin,
    yearQuarterAxisMax,
    axisLabelFormatter
  };
}
